import type {
  AggregatorValidationHistoryEntryView,
  AggregatorValidationItemView,
  AggregatorValidationResponseView,
} from "@credit-acceptance/step6-bff-client";

/**
 * A single field-level CAPS-vs-aggregator comparison row, normalized for the UI.
 */
export interface AggregatorValidationItem {
  fieldName: string;
  capsValue: string;
  aggregatorValue: string;
  mismatch: boolean;
}

/**
 * One past aggregator contract-validation attempt for a VLA, normalized for the UI.
 */
export interface AggregatorValidationHistoryEntry {
  lastUpdatedDatetime: string;
  hasFailedValidations: boolean;
  validations: AggregatorValidationItem[];
}

/**
 * The CAPS-vs-aggregator contract comparison for a deal, normalized for the UI
 * (caps2 Internal tab Aggregator Validation parity). `history` is populated by
 * recap-service via the step6-bff pass-through added in OR-24019; absent
 * scalars/arrays still normalize to empty defaults like any other field
 * rather than being special-cased.
 */
export interface AggregatorValidation {
  aggregatorPartnerName: string;
  hasFailedValidations: boolean;
  lastUpdatedDatetime: string;
  aggregatorDeal: boolean;
  validations: AggregatorValidationItem[];
  history: AggregatorValidationHistoryEntry[];
}

/**
 * Normalizes a single raw `AggregatorValidationItemView` row.
 * @param {AggregatorValidationItemView} raw - A single field-level comparison row.
 * @returns {AggregatorValidationItem} The normalized row.
 */
function toAggregatorValidationItem(raw: AggregatorValidationItemView): AggregatorValidationItem {
  return {
    fieldName: (raw.fieldName ?? "").trim(),
    capsValue: (raw.capsValue ?? "").trim(),
    aggregatorValue: (raw.aggregatorValue ?? "").trim(),
    mismatch: raw.mismatch ?? false,
  };
}

/**
 * Normalizes a single raw `AggregatorValidationHistoryEntryView` row.
 * @param {AggregatorValidationHistoryEntryView} raw - A single past validation attempt.
 * @returns {AggregatorValidationHistoryEntry} The normalized entry.
 */
function toAggregatorValidationHistoryEntry(
  raw: AggregatorValidationHistoryEntryView
): AggregatorValidationHistoryEntry {
  return {
    lastUpdatedDatetime: raw.lastUpdatedDatetime ?? "",
    hasFailedValidations: raw.hasFailedValidations ?? false,
    validations: (raw.validations ?? []).map((item) => toAggregatorValidationItem(item)),
  };
}

/**
 * Normalizes a raw `AggregatorValidationResponseView` into an
 * {@link AggregatorValidation}, coercing absent scalars/arrays to their empty
 * defaults so the render path stays free of optional-field handling.
 * @param {AggregatorValidationResponseView} raw - The BFF response body.
 * @returns {AggregatorValidation} The normalized view model.
 */
export function toAggregatorValidation(raw: AggregatorValidationResponseView): AggregatorValidation {
  return {
    aggregatorPartnerName: raw.aggregatorPartnerName ?? "",
    hasFailedValidations: raw.hasFailedValidations ?? false,
    lastUpdatedDatetime: raw.lastUpdatedDatetime ?? "",
    aggregatorDeal: raw.aggregatorDeal ?? false,
    validations: (raw.validations ?? []).map((item) => toAggregatorValidationItem(item)),
    history: (raw.history ?? []).map((entry) => toAggregatorValidationHistoryEntry(entry)),
  };
}
